import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button, Card, Form, Input, Select, Tabs } from 'antd';
import { useForm } from 'antd/lib/form/Form';

import api from '@services/api';
import DeviceReport from './DeviceReport';
import { Navigation } from '@constants/Navigation';
import { FirmwareReportGroup, ReportForDevice, StbGroup } from '@services/types';
import { selectSearchFilter } from '@services/utils';
import styles from './report.module.scss';
import FirmwareReport from '@layouts/reports/FirmwareReport';
import { StbGroupNameResponse } from '@openapi';

const { TabPane } = Tabs;
const { Option } = Select;

interface FormIstance {
  type: 'SN' | 'MAC' | 'STB';
  value: string;
}

const Reports = () => {
  const paramsRaw = useParams<{ tab: string } & Partial<FormIstance>>();
  const [params, setParams] = React.useState({
    tab: decodeURIComponent(paramsRaw?.tab || ''),
    type: decodeURIComponent(paramsRaw?.type || ''),
    value: decodeURIComponent(paramsRaw?.value || ''),
  });
  const history = useHistory();
  const [form] = useForm<FormIstance>();
  const [report, setReport] = React.useState<ReportForDevice>();
  const [firmwareReport, setFirmwareReport] = React.useState<FirmwareReportGroup>();
  const [stbGroup, setStbGroup] = useState<Array<StbGroupNameResponse>>([]);
  const [stbGroupId, setStbGroupId] = useState<StbGroup['id']>();

  React.useEffect(() => {
    api.stbGroups.getStbGroupsFilter().then((newStbGroup) => {
      setStbGroup(newStbGroup);
    });
  }, []);

  React.useEffect(() => {
    setParams({
      type: decodeURIComponent(paramsRaw?.type || ''),
      value: decodeURIComponent(paramsRaw?.value || ''),
      tab: decodeURIComponent(paramsRaw?.tab || ''),
    });
  }, [paramsRaw]);

  React.useEffect(() => {
    (async () => {
      if (!params.type || !params.value) {
        return;
      }

      if (params.type === 'SN') {
        await api.reports
          .getReport({ serialNumber: params.value })
          .then((data) => {
            setReport(data);
          })
          .catch(() => {
            setReport(undefined);
          });
      } else if (params.type === 'MAC') {
        await api.reports
          .getReport({ macAddress: params.value })
          .then((data) => {
            setReport(data);
          })
          .catch(() => {
            setReport(undefined);
          });
      } else if (stbGroupId === params.value) {
        await api.reports.getFirmwareReport(stbGroupId).then(setFirmwareReport);
      }
    })();
  }, [params, form, stbGroupId]);

  const handleSearch = async () => {
    const values = await form.validateFields();
    {
      params.tab === 'firmware-group'
        ? history.push(
            Navigation.ReportsById.replace(':tab', params.tab)
              .replace(':type?', 'STB')
              .replace(':value?', values.value),
          )
        : history.push(
            Navigation.ReportsById.replace(':tab', params.tab)
              .replace(':type?', encodeURIComponent(values.type))
              .replace(':value?', encodeURIComponent(values.value)),
          );
    }
  };
  const handleInput = async (key: string) => {
    if (key === 'sn-mac') {
      Navigation.ReportsById.replace(':value?', '');
      form.setFieldsValue({ type: 'STB', value: '' });
    } else if (key === 'firmware-group') {
      Navigation.ReportsById.replace(':value?', '');
      form.setFieldsValue({ type: 'SN', value: '' });
      form.setFieldsValue({ type: 'MAC', value: '' });
    }
  };

  return (
    <Tabs
      defaultActiveKey={params.tab || 'group-firmware'}
      onChange={(key) => {
        history.push(Navigation.Reports.replace(':tab', key));
        handleInput(key);
      }}
    >
      <TabPane tab="By Group Firmware" key="firmware-group">
        <Card title="Group-Firmware" style={{ marginBottom: '1rem' }}>
          <Form
            className={styles.form}
            form={form}
            layout="vertical"
            initialValues={{ type: params.type || 'STB', value: params.value || '' }}
          >
            <Form.Item style={{ display: 'none' }} name="type" />
            <Form.Item name="value" label="Select STB Group" style={{ gridArea: 'stbGroup' }}>
              <Select
                onChange={(value: StbGroup['id']) => {
                  setStbGroupId(value);
                  handleSearch();
                }}
                allowClear
                showSearch
                filterOption={selectSearchFilter}
              >
                {stbGroup.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          {stbGroupId && (
            <div style={{ display: 'grid', justifyContent: 'center' }}>
              <Link to={Navigation.GroupMap.replace(':id', stbGroupId)}>Group Map</Link>
            </div>
          )}
        </Card>
        {stbGroupId && firmwareReport && <FirmwareReport report={firmwareReport} />}
      </TabPane>
      <TabPane tab="By SN/MAC" key="sn-mac">
        <Card
          title="Report by Serial Number/MAC address"
          extra={
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
          }
          style={{ marginBottom: '1rem' }}
        >
          <Form form={form} layout="inline" initialValues={{ type: params.type || 'SN', value: params.value || '' }}>
            <Form.Item name="type">
              <Select>
                <Select.Option value="SN">Serial Number</Select.Option>
                <Select.Option value="MAC">MAC address</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="value">
              <Input />
            </Form.Item>
          </Form>
        </Card>
        {report ? (
          <DeviceReport report={report} />
        ) : (
          <div
            style={{
              display: 'grid',
              justifyContent: 'left',
              width: '100%',
              fontSize: '16px',
              color: '#0085fc',
            }}
          >
            <span>Enter the correct search data</span>
          </div>
        )}
      </TabPane>
    </Tabs>
  );
};

export default React.memo(Reports);
